import { useState } from 'react';
import { Plus, Trash2, Edit2, GripVertical, Sparkles, Loader2, ChevronDown, ChevronUp, Check, Link2 } from 'lucide-react';
import type { StudyPlanModule, LearningOutcome, ModuleLiterature, OutcomeESCOMapping } from '@/types';
import { authenticatedFetch } from '@/lib/auth';
import { useConfirm } from './ui';
import ModuleLiteratureAssigner from './ModuleLiteratureAssigner';
import LearningOutcomeEditor from './LearningOutcomeEditor';

interface ModuleEditorProps {
  studyPlanId?: string;
  modules: StudyPlanModule[];
  onChange: (modules: StudyPlanModule[]) => void;
  parentLearningOutcomes?: LearningOutcome;
  parentEscoMappings?: OutcomeESCOMapping[];
  totalEcts?: number;
  totalHours?: number;
  disabled?: boolean;
}

interface ModuleFormData {
  module_code: string;
  title: string;
  description: string;
  ects_credits: number;
  estimated_hours: number;
  learning_outcomes: LearningOutcome;
  prerequisites: string[];
  is_standalone: boolean;
}

const emptyModule: ModuleFormData = {
  module_code: '',
  title: '',
  description: '',
  ects_credits: 0,
  estimated_hours: 0,
  learning_outcomes: { knowledge: [], skills: [], competence: [] },
  prerequisites: [],
  is_standalone: true,
};

export default function ModuleEditor({
  studyPlanId,
  modules,
  onChange,
  parentLearningOutcomes,
  parentEscoMappings = [],
  totalEcts = 0,
  totalHours = 0,
  disabled = false,
}: ModuleEditorProps) {
  const { confirm } = useConfirm();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState<ModuleFormData>(emptyModule);
  const [suggestingModules, setSuggestingModules] = useState(false);
  const [suggestionError, setSuggestionError] = useState('');
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // Module ESCO mappings (keyed by module_code)
  const [moduleEscoMappings, setModuleEscoMappings] = useState<Record<string, OutcomeESCOMapping[]>>({});

  // Calculate totals
  const totalModuleEcts = modules.reduce((sum, m) => sum + (m.ects_credits || 0), 0);
  const totalModuleHours = modules.reduce((sum, m) => sum + (m.estimated_hours || 0), 0);

  // Helper: Find ESCO skills from parent mappings for a given outcome text
  const findParentEscoForOutcome = (outcomeText: string, category: 'knowledge' | 'skills' | 'competence') => {
    if (!parentLearningOutcomes || !parentEscoMappings.length) return [];

    // Find the index of this outcome in parent learning outcomes
    const parentOutcomes = parentLearningOutcomes[category] || [];
    const outcomeIndex = parentOutcomes.findIndex(o => o === outcomeText);

    if (outcomeIndex === -1) return [];

    // Find ESCO mapping for this outcome
    const mapping = parentEscoMappings.find(
      m => m.outcomeType === category && m.outcomeIndex === outcomeIndex
    );

    return mapping?.escoSkills || [];
  };

  const generateModuleCode = () => {
    const existingCodes = modules.map(m => m.module_code);
    let code = 'MOD-A';
    let suffix = 'A';
    while (existingCodes.includes(code)) {
      suffix = String.fromCharCode(suffix.charCodeAt(0) + 1);
      code = `MOD-${suffix}`;
    }
    return code;
  };

  const handleAddModule = () => {
    if (!formData.module_code || !formData.title) return;

    const newModule: StudyPlanModule = {
      ...formData,
      order_index: modules.length,
    };

    onChange([...modules, newModule]);
    setFormData(emptyModule);
    setShowAddForm(false);
  };

  const handleUpdateModule = (index: number) => {
    if (!formData.module_code || !formData.title) return;

    const updatedModules = [...modules];
    updatedModules[index] = {
      ...updatedModules[index],
      ...formData,
    };

    onChange(updatedModules);
    setEditingIndex(null);
    setFormData(emptyModule);
  };

  const handleDeleteModule = async (index: number) => {
    const confirmed = await confirm({
      title: 'Slett modul',
      message: 'Er du sikker på at du vil slette denne modulen?',
      confirmLabel: 'Slett',
      cancelLabel: 'Avbryt',
      variant: 'danger'
    });

    if (!confirmed) return;
    const updatedModules = modules.filter((_, i) => i !== index);
    // Update order indices
    updatedModules.forEach((m, i) => (m.order_index = i));
    onChange(updatedModules);
  };

  const handleEditModule = (index: number) => {
    const module = modules[index];
    setFormData({
      module_code: module.module_code,
      title: module.title,
      description: module.description || '',
      ects_credits: module.ects_credits,
      estimated_hours: module.estimated_hours,
      learning_outcomes: module.learning_outcomes || { knowledge: [], skills: [], competence: [] },
      prerequisites: module.prerequisites || [],
      is_standalone: module.is_standalone,
    });
    setEditingIndex(index);
    setExpandedIndex(index);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const updatedModules = [...modules];
    const [draggedModule] = updatedModules.splice(draggedIndex, 1);
    updatedModules.splice(index, 0, draggedModule);
    updatedModules.forEach((m, i) => (m.order_index = i));

    onChange(updatedModules);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const handleSuggestModules = async () => {
    if (!studyPlanId) {
      setSuggestionError('Studieplanen må lagres først');
      return;
    }

    setSuggestingModules(true);
    setSuggestionError('');

    try {
      const response = await authenticatedFetch(`/api/ai/modules/suggest`, {
        method: 'POST',
        body: JSON.stringify({ study_plan_id: studyPlanId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Kunne ikke generere modulforslag');
      }

      const data = await response.json();

      // API returns suggestedModules, not modules
      const modulesArray = data.suggestedModules || data.modules;
      if (modulesArray && Array.isArray(modulesArray)) {
        const suggestedModules: StudyPlanModule[] = modulesArray.map((m: any, index: number) => ({
          module_code: m.module_code,
          title: m.title,
          description: m.description || '',
          ects_credits: m.ects_credits || 0,
          estimated_hours: m.estimated_hours || 0,
          learning_outcomes: m.learning_outcomes || { knowledge: [], skills: [], competence: [] },
          order_index: index,
          prerequisites: m.prerequisites || [],
          is_standalone: m.is_standalone ?? true,
        }));

        // Replace existing modules with suggestions
        if (modules.length > 0) {
          const replaceConfirmed = await confirm({
            title: 'Erstatt moduler',
            message: 'Dette vil erstatte eksisterende moduler. Fortsette?',
            confirmLabel: 'Erstatt',
            cancelLabel: 'Avbryt',
            variant: 'warning'
          });
          if (!replaceConfirmed) return;
        }

        onChange(suggestedModules);
      }
    } catch (err: any) {
      setSuggestionError(err.message);
    } finally {
      setSuggestingModules(false);
    }
  };

  const togglePrerequisite = (moduleCode: string) => {
    const prerequisites = formData.prerequisites || [];
    if (prerequisites.includes(moduleCode)) {
      setFormData({
        ...formData,
        prerequisites: prerequisites.filter(p => p !== moduleCode),
      });
    } else {
      setFormData({
        ...formData,
        prerequisites: [...prerequisites, moduleCode],
      });
    }
  };

  const renderModuleForm = (isEditing: boolean, index?: number) => (
    <div className="bg-gray-50 p-4 rounded-lg border space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Modulkode</label>
          <input
            type="text"
            className="input w-full"
            value={formData.module_code}
            onChange={e => setFormData({ ...formData, module_code: e.target.value })}
            placeholder="F.eks. MOD-A eller HMS-GRUNN"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Tittel</label>
          <input
            type="text"
            className="input w-full"
            value={formData.title}
            onChange={e => setFormData({ ...formData, title: e.target.value })}
            placeholder="Modulens tittel"
            disabled={disabled}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Beskrivelse</label>
        <textarea
          className="input w-full"
          rows={2}
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          placeholder="Kort beskrivelse av modulen"
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Kompetansepoeng</label>
          <input
            type="number"
            step="0.5"
            min="0"
            className="input w-full"
            value={formData.ects_credits}
            onChange={e => setFormData({ ...formData, ects_credits: parseFloat(e.target.value) || 0 })}
            disabled={disabled}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Estimerte timer</label>
          <input
            type="number"
            min="0"
            className="input w-full"
            value={formData.estimated_hours}
            onChange={e => setFormData({ ...formData, estimated_hours: parseInt(e.target.value) || 0 })}
            disabled={disabled}
          />
        </div>
      </div>

      {/* Learning outcomes with ESCO */}
      <div className="space-y-3">
        <h4 className="font-medium text-sm">Læringsutbytte for denne modulen</h4>
        <LearningOutcomeEditor
          learning_outcomes={formData.learning_outcomes}
          onChange={(outcomes) => setFormData({ ...formData, learning_outcomes: outcomes })}
          escoMappings={moduleEscoMappings[formData.module_code] || []}
          onESCOMappingsChange={(mappings) => {
            setModuleEscoMappings(prev => ({
              ...prev,
              [formData.module_code]: mappings,
            }));
          }}
          disabled={disabled}
          showAutoSuggest={true}
          labelPrefix={formData.module_code}
        />
      </div>

      {/* Prerequisites */}
      {modules.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-1">Forkunnskapskrav (andre moduler)</label>
          <div className="flex flex-wrap gap-2">
            {modules
              .filter(m => m.module_code !== formData.module_code)
              .map(m => (
                <button
                  key={m.module_code}
                  type="button"
                  className={`px-2 py-1 text-sm rounded border ${
                    formData.prerequisites.includes(m.module_code)
                      ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : 'bg-gray-100 border-gray-200'
                  }`}
                  onClick={() => togglePrerequisite(m.module_code)}
                  disabled={disabled}
                >
                  {m.module_code}: {m.title}
                </button>
              ))}
          </div>
        </div>
      )}

      {/* Standalone toggle */}
      <div className="flex items-center gap-2">
        <input
          type="checkbox"
          id="is_standalone"
          checked={formData.is_standalone}
          onChange={e => setFormData({ ...formData, is_standalone: e.target.checked })}
          disabled={disabled}
        />
        <label htmlFor="is_standalone" className="text-sm">
          Kan tas som selvstendig modul (uten fullføring av hele planen)
        </label>
      </div>

      {/* Form buttons */}
      <div className="flex gap-2 justify-end">
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => {
            setFormData(emptyModule);
            setShowAddForm(false);
            setEditingIndex(null);
          }}
        >
          Avbryt
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => (isEditing && index !== undefined ? handleUpdateModule(index) : handleAddModule())}
          disabled={!formData.module_code || !formData.title || disabled}
        >
          <Check size={16} />
          {isEditing ? 'Oppdater modul' : 'Legg til modul'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Header with totals and AI button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600">
          <span className={totalModuleEcts > totalEcts ? 'text-red-600 font-bold' : ''}>
            {totalModuleEcts} / {totalEcts} KP
          </span>
          {' | '}
          <span className={totalModuleHours > totalHours ? 'text-red-600 font-bold' : ''}>
            {totalModuleHours} / {totalHours} timer
          </span>
        </div>

        <button
          type="button"
          className="btn btn-secondary flex items-center gap-2"
          onClick={handleSuggestModules}
          disabled={suggestingModules || !studyPlanId || disabled}
        >
          {suggestingModules ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <Sparkles size={16} />
          )}
          Foreslå moduler med KI
        </button>
      </div>

      {suggestionError && (
        <div className="bg-red-50 text-red-600 p-2 rounded text-sm">
          {suggestionError}
        </div>
      )}

      {/* Module list */}
      <div className="space-y-2">
        {modules.map((module, index) => (
          <div
            key={module.module_code || index}
            className={`border rounded-lg ${draggedIndex === index ? 'opacity-50' : ''} ${
              expandedIndex === index ? 'bg-white' : 'bg-gray-50'
            }`}
            draggable={!disabled}
            onDragStart={() => handleDragStart(index)}
            onDragOver={e => handleDragOver(e, index)}
            onDragEnd={handleDragEnd}
          >
            {/* Module header */}
            <div className="flex items-center gap-2 p-3">
              <GripVertical size={16} className="text-gray-400 cursor-grab" />
              <span className="font-mono text-sm bg-gray-200 px-2 py-0.5 rounded">
                {module.module_code}
              </span>
              <span className="font-medium flex-1">{module.title}</span>
              <span className="text-sm text-gray-500">
                {module.ects_credits} KP | {module.estimated_hours}t
              </span>
              {module.is_standalone && (
                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                  Selvstendig
                </span>
              )}
              <button
                type="button"
                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                className="p-1 hover:bg-gray-200 rounded"
              >
                {expandedIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              <button
                type="button"
                onClick={() => handleEditModule(index)}
                className="p-1 hover:bg-blue-100 rounded text-blue-600"
                disabled={disabled}
              >
                <Edit2 size={16} />
              </button>
              <button
                type="button"
                onClick={() => handleDeleteModule(index)}
                className="p-1 hover:bg-red-100 rounded text-red-600"
                disabled={disabled}
              >
                <Trash2 size={16} />
              </button>
            </div>

            {/* Expanded content */}
            {expandedIndex === index && editingIndex !== index && (
              <div className="px-4 pb-4 border-t">
                {module.description && (
                  <p className="text-sm text-gray-600 mt-2">{module.description}</p>
                )}

                {/* Learning outcomes with inherited ESCO */}
                {module.learning_outcomes && (
                  <div className="mt-3 space-y-3">
                    {(module.learning_outcomes.knowledge?.length ?? 0) > 0 && (
                      <div>
                        <span className="text-xs font-medium text-blue-600">Kunnskap:</span>
                        <div className="space-y-2 mt-1">
                          {module.learning_outcomes.knowledge?.map((item, i) => {
                            const escoSkills = findParentEscoForOutcome(item, 'knowledge');
                            return (
                              <div key={i} className="text-sm">
                                <div className="flex items-start gap-2">
                                  <span className="text-gray-400">•</span>
                                  <span>{item}</span>
                                </div>
                                {escoSkills.length > 0 && (
                                  <div className="ml-4 mt-1 flex flex-col gap-1">
                                    {escoSkills.map(skill => (
                                      <div key={skill.uri} className="flex items-start gap-1.5 text-xs text-emerald-700 bg-emerald-50 px-2 py-1 rounded border border-emerald-200">
                                        <Link2 size={10} className="flex-shrink-0 mt-0.5" />
                                        <span>{skill.title}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(module.learning_outcomes.skills?.length ?? 0) > 0 && (
                      <div>
                        <span className="text-xs font-medium text-green-600">Ferdigheter:</span>
                        <div className="space-y-2 mt-1">
                          {module.learning_outcomes.skills?.map((item, i) => {
                            const escoSkills = findParentEscoForOutcome(item, 'skills');
                            return (
                              <div key={i} className="text-sm">
                                <div className="flex items-start gap-2">
                                  <span className="text-gray-400">•</span>
                                  <span>{item}</span>
                                </div>
                                {escoSkills.length > 0 && (
                                  <div className="ml-4 mt-1 flex flex-col gap-1">
                                    {escoSkills.map(skill => (
                                      <div key={skill.uri} className="flex items-start gap-1.5 text-xs text-blue-700 bg-blue-50 px-2 py-1 rounded border border-blue-200">
                                        <Link2 size={10} className="flex-shrink-0 mt-0.5" />
                                        <span>{skill.title}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {(module.learning_outcomes.competence?.length ?? 0) > 0 && (
                      <div>
                        <span className="text-xs font-medium text-purple-600">Generell kompetanse:</span>
                        <div className="space-y-2 mt-1">
                          {module.learning_outcomes.competence?.map((item, i) => {
                            const escoSkills = findParentEscoForOutcome(item, 'competence');
                            return (
                              <div key={i} className="text-sm">
                                <div className="flex items-start gap-2">
                                  <span className="text-gray-400">•</span>
                                  <span>{item}</span>
                                </div>
                                {escoSkills.length > 0 && (
                                  <div className="ml-4 mt-1 flex flex-col gap-1">
                                    {escoSkills.map(skill => (
                                      <div key={skill.uri} className="flex items-start gap-1.5 text-xs text-violet-700 bg-violet-50 px-2 py-1 rounded border border-violet-200">
                                        <Link2 size={10} className="flex-shrink-0 mt-0.5" />
                                        <span>{skill.title}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Prerequisites */}
                {(module.prerequisites?.length ?? 0) > 0 && (
                  <div className="mt-3">
                    <span className="text-xs font-medium text-gray-600">Forkunnskapskrav:</span>
                    <div className="flex gap-1 mt-1">
                      {module.prerequisites?.map(prereq => (
                        <span key={prereq} className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded">
                          {prereq}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Literature assignments */}
                {studyPlanId && (
                  <div className="mt-4 pt-3 border-t">
                    <ModuleLiteratureAssigner
                      studyPlanId={studyPlanId}
                      moduleCode={module.module_code}
                      moduleId={module.id}
                      moduleData={module}
                      assignments={module.literature || []}
                      onChange={(newAssignments: ModuleLiterature[]) => {
                        const updatedModules = [...modules];
                        updatedModules[index] = {
                          ...updatedModules[index],
                          literature: newAssignments,
                        };
                        onChange(updatedModules);
                      }}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
            )}

            {/* Edit form */}
            {editingIndex === index && (
              <div className="px-4 pb-4 border-t pt-3">
                {renderModuleForm(true, index)}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Add module form */}
      {showAddForm ? (
        renderModuleForm(false)
      ) : (
        <button
          type="button"
          className="btn btn-secondary w-full flex items-center justify-center gap-2"
          onClick={() => {
            setFormData({ ...emptyModule, module_code: generateModuleCode() });
            setShowAddForm(true);
          }}
          disabled={disabled}
        >
          <Plus size={16} />
          Legg til modul
        </button>
      )}

      {/* Validation messages */}
      {totalModuleEcts > totalEcts && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
          Advarsel: Modulenes totale kompetansepoeng ({totalModuleEcts}) overstiger studieplanens totale kompetansepoeng ({totalEcts})
        </div>
      )}
      {totalModuleHours > totalHours && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-2 rounded text-sm">
          Advarsel: Modulenes totale timer ({totalModuleHours}) overstiger studieplanens estimerte timer ({totalHours})
        </div>
      )}
    </div>
  );
}
